import { GeometryBuilder } from './geometry-builder';
export type BoatFamily = 'ferry' | 'work' | 'launch' | 'skiff' | 'landing';
export const BOAT_SPECS: Record<
  BoatFamily,
  { width: number; length: number; height: number; speed: number; seats: number; armour?: number }
> = {
  ferry: { width: 2.6, length: 6.8, height: 2.45, speed: 4.1, seats: 6 },
  work: { width: 2.5, length: 6.2, height: 2.5, speed: 3.5, seats: 0 },
  launch: { width: 2.2, length: 5.6, height: 2.35, speed: 4.3, seats: 3 },
  skiff: { width: 1.8, length: 4.5, height: 1.45, speed: 3.2, seats: 0 },
  landing: { width: 11.5, length: 36, height: 6.2, speed: 5.8, seats: 100, armour: 95 },
};
export const BOAT_FAMILIES: BoatFamily[] = ['ferry', 'work', 'launch', 'skiff'];
export const RENDER_BOAT_FAMILIES: BoatFamily[] = [...BOAT_FAMILIES, 'landing'];
export function boatGeometry(kind: BoatFamily) {
  const s = BOAT_SPECS[kind],
    g = new GeometryBuilder(),
    w = s.width,
    l = s.length;
  if (kind === 'landing') {
    for (let section = 0; section < 18; section++) {
      const z = -18 + section * 2,
        span = section < 3 ? 8 + section * 1.15 : 11.5;
      g.box(-span / 2, 0.08, z, span, 0.7, 2, 0x8b9594);
      g.box(-span / 2, 0.78, z, span, 0.1, 2, 0xa8b1a6);
      for (const side of [-1, 1]) {
        // A side opening aligns with the supported landing gangway.
        if (section !== 8 && section !== 9) g.box((side * span) / 2 - 0.08, 0.88, z, 0.16, 1.2, 2, 0x89918a);
        g.box((side * span) / 2 - 0.08, 0.45, z, 0.16, 0.12, 2, 0x4a5656);
      }
    }
    for (let row = 0; row < 13; row++)
      for (let column = 0; column < 8; column++) {
        const x = column < 4 ? -0.85 - (3 - column) * 1.1 : 0.85 + (column - 4) * 1.1,
          z = -15 + row * 1.75;
        g.box(x - 0.27, 0.88, z - 0.25, 0.54, 0.42, 0.5, 0x65746a);
        g.box(x - 0.27, 1.3, z + 0.2, 0.54, 0.48, 0.06, 0x7e8b7e);
      }
    // Two open-deck operating consoles belong to the original forward crew
    // seats; the inspection craft cannot sail with both operators absent.
    for (const x of [-0.85, 0.85]) {
      g.box(x - 0.3, 0.88, -16.4, 0.6, 0.95, 0.38, 0x566960);
      g.box(x - 0.24, 1.83, -16.38, 0.48, 0.04, 0.32, 0x8ba3a0);
      g.box(x - 0.15, 1.92, -16.1, 0.3, 0.04, 0.1, 0x263d3a);
    }
    g.box(-4, 0.88, 9, 8, 2.25, 7, 0x929d93);
    g.box(-3.6, 3.13, 10, 7.2, 0.16, 6, 0xb9c0ad);
    for (const x of [-3.7, 3.5]) g.box(x, 3.29, 10, 0.2, 1.5, 6, 0x7f8e85);
    g.box(-3.5, 3.29, 15.8, 7, 0.2, 0.2, 0x75877b);
    g.box(-3.5, 3.7, 10, 7, 0.8, 0.1, 0x517a84);
    g.box(-3.8, 4.8, 9.7, 7.6, 0.2, 6.6, 0xb9c0ad);
    g.box(-0.06, 5, 12, 0.12, 1.2, 0.12, 0x586e67);
    g.box(-1.2, 5.9, 12, 2.4, 0.15, 0.12, 0x7f9182);
    for (const side of [-1, 1]) {
      g.box(side < 0 ? -6.1 : 5.2, 0.7, -1.3, 0.9, 0.18, 2.6, 0x9ca793);
      for (const z of [-15, -10, -5, 5, 10, 15])
        g.box(side * 5.65 - 0.12, 0.2, z - 0.4, 0.24, 0.55, 0.8, 0x475452);
    }
    return g.geometry();
  }
  // Narrowed bow/chines, rubbing strakes, deck, glazed wheelhouse and fitted roles.
  for (let i = 0; i < 8; i++) {
    const t = i / 7,
      span = w * (0.5 + 0.5 * Math.sin(Math.PI * (0.1 + 0.8 * t)));
    g.box(-span / 2, 0.08, -l / 2 + (i * l) / 8, span, 0.47, l / 8, 0xc3d4cd);
    g.box(-span / 2, 0.48, -l / 2 + (i * l) / 8, span, 0.3, l / 8, kind === 'work' ? 0x6b8686 : 0xf0e9d6);
  }
  g.box(-w * 0.42, 0.8, -l * 0.38, w * 0.84, 0.08, l * 0.76, 0xb8a685);
  for (const x of [-1, 1]) {
    g.box(x * w * 0.45 - 0.035, 0.9, -l * 0.3, 0.07, 0.18, l * 0.6, 0x7f9690);
    for (const z of [-0.24, 0.22]) g.box(x * w * 0.49 - 0.09, 0.45, l * z, 0.18, 0.36, 0.45, 0x566560);
  }
  if (kind === 'skiff') {
    for (const z of [-0.22, 0.2]) g.box(-w * 0.37, 0.96, l * z, w * 0.74, 0.12, 0.42, 0xd0b98f);
    g.box(-0.22, 0.4, l * 0.47, 0.44, 0.85, 0.3, 0x5e7675);
    for (const side of [-1, 1]) g.box(side * (w * 0.42) - 0.06, 0.96, -1.1, 0.12, 0.08, 2.7, 0x99845f);
  } else {
    const cab = kind === 'work' ? 0.27 : kind === 'launch' ? 0.36 : 0.6;
    g.box(-w * 0.36, 0.9, -l * 0.28, w * 0.72, 0.9, l * cab, 0xe1e4cf);
    g.box(-w * 0.34, 1.25, -l * 0.28 - 0.02, w * 0.68, 0.5, 0.045, 0x4a7b86);
    for (const side of [-1, 1])
      g.box(side * w * 0.36 - 0.025, 1.25, -l * 0.23, 0.05, 0.49, l * (cab - 0.08), 0x587f89);
    g.box(-w * 0.4, 1.85, -l * 0.3, w * 0.8, 0.1, l * (cab + 0.04), 0xf5ead1);
    g.box(-0.025, 1.95, -l * 0.15, 0.05, s.height - 1.95, 0.05, 0x506e74);
    if (kind === 'ferry')
      for (const x of [-0.6, 0.6])
        for (const z of [0.4, 1.2, 2]) {
          g.box(x - 0.2, 0.87, z, 0.4, 0.35, 0.32, 0x6e9393);
          g.box(x - 0.2, 1.12, z + 0.25, 0.4, 0.38, 0.07, 0x6e9393);
        }
    if (kind === 'work') {
      g.box(-0.15, 0.9, l * 0.27, 0.3, 1.05, 0.3, 0x638985);
      g.box(-0.12, 1.9, l * 0.07, 0.24, 0.15, l * 0.38, 0x638985);
    }
  }
  g.box(-w * 0.3, 1.04, -l * 0.37, 0.13, 0.13, 0.12, 0xcd9079);
  g.box(w * 0.3 - 0.13, 1.04, -l * 0.37, 0.13, 0.13, 0.12, 0x8aba9c);
  return g.geometry();
}
